import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { ActionMenu } from '../AppToolbar';
import { runLoading } from '../loading';
import { useNotify } from '../notifications';

/** One Reports ▾ entry — the shape of the shared `IFormReportReader.FormReportItem`. */
export interface FormReportItem {
  id: number;
  label: string;
  templateName: string;
  allowMultipleJobId?: boolean;
}

export interface FormReportsMenuProps {
  /** Endpoint returning the reports the lab assigned to this view
   *  (`{ success, data: FormReportItem[] }`), e.g. `/api/v1/reagent-preps/reports?view=list`. */
  reportsUrl: string;
  /** Endpoint that renders `templateName` for the selected keys and returns the PDF. */
  pdfUrl: string;
  /** Query parameter the keys are sent as, comma-joined (e.g. `llts`). */
  keysParam: string;
  /** The record keys the report runs for — the ticked rows, or the open record. */
  selectedKeys: string[];
  /** OPT-IN (G5 parity, 2026-11-03): keep the button clickable with nothing
   *  selected, as GoLive's Reports action is; picking a report then says
   *  `noSelectionMessage` instead of running. Unset = greyed until a row is
   *  ticked (unchanged). First used by Reagent Preparation. */
  enabledWithoutSelection?: boolean;
  /** Shown when a report is picked with nothing selected (with `enabledWithoutSelection`). */
  noSelectionMessage?: string;
}

/**
 * The shared Reports ▾ dropdown (GoLive `FormReportPermissionViewController`).
 * The entries are whatever the lab assigned to the view in Form Report
 * Permission — never hardcoded — and the button HIDES when nothing is assigned
 * (GoLive parity). Business rules (e.g. "exactly one record for this template")
 * live on the server; their message is shown verbatim.
 *
 * The popup itself is the shared {@link ActionMenu}, so placement, styling and
 * the rule-14 keyboard contract are inherited rather than re-implemented.
 */
export function FormReportsMenu({
  reportsUrl, pdfUrl, keysParam, selectedKeys,
  enabledWithoutSelection = false, noSelectionMessage = 'Please select an item to proceed further.',
}: FormReportsMenuProps) {
  const notify = useNotify();
  const [items, setItems] = useState<FormReportItem[]>([]);

  useEffect(() => {
    let live = true;
    api.get(reportsUrl, { silentLoading: true } as never)
      .then(res => { if (live) setItems(((res.data as { data?: FormReportItem[] })?.data) ?? []); })
      // No assignment / no access → the button stays hidden (GoLive parity).
      .catch(() => { if (live) setItems([]); });
    return () => { live = false; };
  }, [reportsUrl]);

  const run = useCallback(async (it: FormReportItem) => {
    if (selectedKeys.length === 0) { notify.warning(noSelectionMessage); return; }
    try {
      const res = await runLoading(
        api.get(pdfUrl, {
          params: { templateName: it.templateName, [keysParam]: selectedKeys.join(',') },
          responseType: 'blob',
        }),
        { blocking: true, label: `Generating ${it.label}…` });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) {
        notify.warning(`No data to render for ${it.label}.`);
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      // A blob request hands back its JSON error body as a Blob — read the server's message.
      let message = `Failed to generate ${it.label}.`;
      const body = (err as { response?: { data?: unknown } })?.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text()) as { error?: { message?: string } };
          if (parsed?.error?.message) message = parsed.error.message;
        } catch { /* not JSON — keep the generic message */ }
      }
      notify.error(message);
    }
  }, [selectedKeys, pdfUrl, keysParam, notify, noSelectionMessage]);

  if (items.length === 0) return null;

  const greyed = selectedKeys.length === 0 && !enabledWithoutSelection;
  return (
    <ActionMenu
      label="Reports"
      icon="ti-file-text"
      title={greyed ? 'Select at least one row' : 'Reports'}
      disabled={greyed}
      items={items.map(it => ({
        id: String(it.id),
        label: it.label,
        icon: 'ti-file-text',
        onSelect: () => { void run(it); },
      }))}
    />
  );
}
